// src/api/api.ts

import { AxiosError, AxiosResponse } from 'axios';

import { i18n } from '@/i18n';

import axiosInstance from './axiosInstance';

/**
 * Handle a request using Axios and return the data from the response.
 */
export const handleRequest = <T>(promise: Promise<AxiosResponse<T>>): Promise<T> => {
  return promise
    .then((response) => response.data)
    .catch((error) => {
      throw buildError(error);
    });
};

/**
 * Build an error message from an Axios error object.
 */
const buildError = (error: AxiosError): string => {
  const genericErrorMessage = i18n.t('common:apiErrors.generic');

  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx

    // Error object with content-type text/html so we return a generic message
    const contentType = error.response.headers['content-type'];
    if (typeof contentType === 'string' && contentType.includes('text/html')) {
      return genericErrorMessage;
    }

    // Error object with detail prop from API
    if (error.response.data && typeof error.response.data === 'object' && 'detail' in error.response.data) {
      return (error.response.data as any).detail;
    }

    // Error object returned from API
    if (error.response.data) {
      return Object.entries(error.response.data).reduce((acc, [key, value]) => `${acc}${acc === '' ? '' : ', '}${key}: ${value}`, '');
    }
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    return i18n.t('common:apiErrors.noResponse');
  } else {
    // Something happened in setting up the request that triggered an Error
    const message = typeof error === 'string' ? error : error.message;
    return message || genericErrorMessage;
  }
  return genericErrorMessage;
};

/**
 * API service with methods to make requests using Axios.
 */
export const api = {
  get: async <T = any>(url: string, config?: Parameters<typeof axiosInstance.get>[1]) =>
    handleRequest<T>(axiosInstance.get<T>(url, config)),
  post: async <T = any>(url: string, data?: any, config?: Parameters<typeof axiosInstance.post>[2]) =>
    handleRequest<T>(axiosInstance.post<T>(url, data, config)),
  put: async <T = any>(url: string, data?: any, config?: Parameters<typeof axiosInstance.put>[2]) =>
    handleRequest<T>(axiosInstance.put<T>(url, data, config)),
  patch: async <T = any>(url: string, data?: any, config?: Parameters<typeof axiosInstance.patch>[2]) =>
    handleRequest<T>(axiosInstance.patch<T>(url, data, config)),
  delete: async <T = any>(url: string, config?: Parameters<typeof axiosInstance.delete>[1]) =>
    handleRequest<T>(axiosInstance.delete<T>(url, config)),
};
